import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

export type AddEmailDomainOutput =
	OutputRouter['emailDomains']['addEmailDomain']
export type CheckEmailDomainOutput =
	OutputRouter['emailDomains']['checkEmailDomain']
export type GetEmailDomainOutput =
	OutputRouter['emailDomains']['getEmailDomain']
export type DeleteEmailDomainOutput =
	OutputRouter['emailDomains']['deleteEmailDomain']

export type AddEmailDomainInput =
	InputRouter['emailDomains']['addEmailDomain']
export type CheckEmailDomainInput =
	InputRouter['emailDomains']['checkEmailDomain']
export type GetEmailDomainInput = InputRouter['emailDomains']['getEmailDomain']
export type DeleteEmailDomainInput =
	InputRouter['emailDomains']['deleteEmailDomain']

export const useGetEmailDomain = (storeId: string, enabled = true) => {
	return trpc.emailDomains.getEmailDomain.useQuery(storeId, {
		enabled: enabled && !!storeId,
	})
}

export const useAddEmailDomain = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.emailDomains.addEmailDomain.useMutation({
		onSuccess: () => {
			utils.emailDomains.getEmailDomain.invalidate()
			toast({
				title: 'Success',
				description: 'Email domain added successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to add email domain',
				variant: 'destructive',
			})
		},
	})
}

export const useCheckEmailDomain = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.emailDomains.checkEmailDomain.useMutation({
		onSuccess: (data) => {
			utils.emailDomains.getEmailDomain.invalidate()
			if (data.success && data.domain?.status === 'verified') {
				toast({
					title: 'Success',
					description: 'Domain verified successfully',
				})
			} else {
				toast({
					title: 'Verification Pending',
					description: `Status: ${data.domain?.status || 'pending'}`,
				})
			}
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to verify email domain',
				variant: 'destructive',
			})
		},
	})
}

export const useDeleteEmailDomain = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.emailDomains.deleteEmailDomain.useMutation({
		onSuccess: () => {
			utils.emailDomains.getEmailDomain.invalidate()
			toast({
				title: 'Success',
				description: 'Email domain deleted successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to delete email domain',
				variant: 'destructive',
			})
		},
	})
}

